import { useState, useEffect, useRef } from "react";
import { Search, Sparkles, ChevronRight } from "lucide-react";

interface CommandItem {
  question: string;
  answer: string;
  category: string;
}

interface CommandPaletteProps {
  isOpen: boolean;
  onClose: () => void;
  commands: CommandItem[];
  onOpenAIChat?: (question?: string) => void;
}

export function CommandPalette({ isOpen, onClose, commands, onOpenAIChat }: CommandPaletteProps) {
  const [searchQuery, setSearchQuery] = useState("");
  const [selectedIndex, setSelectedIndex] = useState(0);
  const [selectedCommand, setSelectedCommand] = useState<CommandItem | null>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  const filteredCommands = commands.filter((cmd) =>
    cmd.question.toLowerCase().includes(searchQuery.toLowerCase()) ||
    cmd.category.toLowerCase().includes(searchQuery.toLowerCase())
  );

  useEffect(() => {
    if (isOpen && inputRef.current) {
      inputRef.current.focus();
    }
  }, [isOpen]);

  useEffect(() => {
    setSelectedIndex(0);
  }, [searchQuery]);

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === "ArrowDown") {
      e.preventDefault();
      setSelectedIndex((prev) => (prev + 1) % filteredCommands.length);
    } else if (e.key === "ArrowUp") {
      e.preventDefault();
      setSelectedIndex((prev) => (prev - 1 + filteredCommands.length) % filteredCommands.length);
    } else if (e.key === "Enter" && filteredCommands[selectedIndex]) {
      e.preventDefault();
      setSelectedCommand(filteredCommands[selectedIndex]);
    } else if (e.key === "Escape") {
      if (selectedCommand) {
        setSelectedCommand(null);
      } else {
        onClose();
      }
    }
  };

  if (!isOpen) return null;

  return (
    <div
      className="fixed inset-0 z-50 flex items-start justify-center pt-32"
      style={{ backgroundColor: 'rgba(0, 0, 0, 0.5)' }}
      onClick={() => {
        if (selectedCommand) {
          setSelectedCommand(null);
        } else {
          onClose();
        }
      }}
    >
      {!selectedCommand ? (
        <div
          className="rounded-lg shadow-2xl"
          style={{
            backgroundColor: 'var(--card)',
            border: '1px solid var(--border)',
            width: '90%',
            maxWidth: '640px',
          }}
          onClick={(e) => e.stopPropagation()}
        >
          {/* Search Input */}
          <div
            className="flex items-center gap-3 px-4 py-4"
            style={{
              borderBottom: '1px solid var(--border)',
            }}
          >
            <Search size={20} style={{ color: 'var(--muted-foreground)' }} />
            <input
              ref={inputRef}
              type="text"
              placeholder="Ask anything about Brand Monitoring metrics..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              onKeyDown={handleKeyDown}
              className="flex-1 bg-transparent outline-none"
              style={{
                color: 'var(--foreground)',
                fontSize: 'var(--text-base)',
              }}
            />
            <kbd
              className="px-2 py-1 rounded text-xs"
              style={{
                backgroundColor: 'var(--muted)',
                color: 'var(--muted-foreground)',
                border: '1px solid var(--border)',
              }}
            >
              ESC
            </kbd>
          </div>

          {/* Results */}
          <div
            style={{
              maxHeight: '400px',
              overflowY: 'auto',
            }}
          >
            {filteredCommands.length > 0 ? (
              <div>
                {filteredCommands.map((cmd, index) => (
                  <button
                    key={index}
                    onClick={() => setSelectedCommand(cmd)}
                    className="w-full text-left px-4 py-3 transition-colors flex items-center justify-between group"
                    style={{
                      backgroundColor: selectedIndex === index ? 'var(--muted)' : 'transparent',
                      borderBottom: '1px solid var(--border)',
                      border: 'none',
                      cursor: 'pointer',
                    }}
                    onMouseEnter={() => setSelectedIndex(index)}
                  >
                    <div className="flex-1">
                      <div style={{ color: 'var(--foreground)', fontWeight: 'var(--font-weight-medium)', marginBottom: 'var(--spacing-1)' }}>
                        {cmd.question}
                      </div>
                      <div style={{ color: 'var(--muted-foreground)', fontSize: 'var(--text-sm)' }}>
                        {cmd.category}
                      </div>
                    </div>
                    <ChevronRight
                      size={16}
                      style={{
                        color: 'var(--muted-foreground)',
                        opacity: selectedIndex === index ? 1 : 0,
                        transition: 'opacity 0.2s',
                      }}
                    />
                  </button>
                ))}
              </div>
            ) : (
              <div className="px-4 py-8 text-center">
                <p style={{ color: 'var(--muted-foreground)' }}>
                  This search only covers a set of common questions, and that one isn't in it. For anything else, ask Upmode chat.
                </p>
                {onOpenAIChat && (
                  <button
                    onClick={() => {
                      onOpenAIChat(searchQuery || undefined);
                      onClose();
                    }}
                    className="mt-4 rounded-lg px-4 py-2 inline-flex items-center gap-2"
                    style={{
                      backgroundColor: 'var(--accent)',
                      color: 'var(--accent-foreground)',
                      border: 'none',
                      cursor: 'pointer',
                      fontWeight: 'var(--font-weight-medium)',
                    }}
                  >
                    Ask Upmode chat →
                  </button>
                )}
              </div>
            )}
          </div>

          {/* Footer */}
          <div
            className="px-4 py-3 flex items-center justify-between text-xs"
            style={{
              backgroundColor: 'var(--muted)',
              color: 'var(--muted-foreground)',
              borderTop: '1px solid var(--border)',
            }}
          >
            <div className="flex items-center gap-4">
              <div className="flex items-center gap-1">
                <kbd className="px-1.5 py-0.5 rounded" style={{ backgroundColor: 'var(--background)', border: '1px solid var(--border)' }}>↑</kbd>
                <kbd className="px-1.5 py-0.5 rounded" style={{ backgroundColor: 'var(--background)', border: '1px solid var(--border)' }}>↓</kbd>
                <span>to navigate</span>
              </div>
              <div className="flex items-center gap-1">
                <kbd className="px-1.5 py-0.5 rounded" style={{ backgroundColor: 'var(--background)', border: '1px solid var(--border)' }}>↵</kbd>
                <span>to select</span>
              </div>
            </div>
          </div>
        </div>
      ) : (
        <div
          className="rounded-lg shadow-2xl p-8"
          style={{
            backgroundColor: 'var(--card)',
            border: '1px solid var(--border)',
            width: '90%',
            maxWidth: '640px',
            maxHeight: '80vh',
            overflow: 'auto',
          }}
          onClick={(e) => e.stopPropagation()}
        >
          {/* Question */}
          <div className="mb-6">
            <div className="flex items-center gap-2 mb-3">
              <div
                className="w-8 h-8 rounded-full flex items-center justify-center"
                style={{
                  backgroundColor: 'var(--muted)',
                  color: 'var(--signal-blue)',
                }}
              >
                <Sparkles size={16} />
              </div>
              <h3 style={{ color: 'var(--foreground)' }}>
                {selectedCommand.question}
              </h3>
            </div>
            <div
              className="inline-block px-2 py-1 rounded text-xs"
              style={{
                backgroundColor: 'var(--muted)',
                color: 'var(--muted-foreground)',
              }}
            >
              {selectedCommand.category}
            </div>
          </div>

          {/* Answer */}
          <div
            className="rounded-lg p-4 mb-6"
            style={{
              backgroundColor: 'var(--muted)',
              border: '1px solid var(--border)',
            }}
          >
            <p style={{ color: 'var(--foreground)', lineHeight: '1.6' }}>
              {selectedCommand.answer}
            </p>
          </div>

          {/* Actions */}
          <div className="flex gap-3">
            <button
              onClick={() => setSelectedCommand(null)}
              className="flex-1 rounded-lg px-4 py-3 transition-colors"
              style={{
                backgroundColor: 'var(--muted)',
                color: 'var(--foreground)',
                border: '1px solid var(--border)',
                cursor: 'pointer',
                fontWeight: 'var(--font-weight-medium)',
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.backgroundColor = 'var(--accent)';
                e.currentTarget.style.color = 'var(--accent-foreground)';
                e.currentTarget.style.borderColor = 'var(--accent)';
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.backgroundColor = 'var(--muted)';
                e.currentTarget.style.color = 'var(--foreground)';
                e.currentTarget.style.borderColor = 'var(--border)';
              }}
            >
              ← Back to search
            </button>
            {onOpenAIChat && (
              <button
                onClick={() => {
                  onOpenAIChat(selectedCommand.question);
                  onClose();
                }}
                className="flex-1 rounded-lg px-4 py-3 transition-colors flex items-center justify-center gap-2"
                style={{
                  backgroundColor: 'var(--accent)',
                  color: 'var(--accent-foreground)',
                  border: 'none',
                  cursor: 'pointer',
                  fontWeight: 'var(--font-weight-medium)',
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.opacity = '0.9';
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.opacity = '1';
                }}
              >
                Chat with AI →
              </button>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

// Hook to enable the command palette with keyboard shortcut
export function useCommandPalette() {
  const [isOpen, setIsOpen] = useState(false);

  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      // "/" key or Cmd+K / Ctrl+K
      if (e.key === "/" && !e.metaKey && !e.ctrlKey) {
        // Only if not in an input field
        if (document.activeElement?.tagName !== "INPUT" && document.activeElement?.tagName !== "TEXTAREA") {
          e.preventDefault();
          setIsOpen(true);
        }
      } else if ((e.metaKey || e.ctrlKey) && e.key === "k") {
        e.preventDefault();
        setIsOpen(true);
      }
    };

    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, []);

  const openCommandPalette = () => setIsOpen(true);
  const closeCommandPalette = () => setIsOpen(false);

  return { isOpen, setIsOpen, openCommandPalette, closeCommandPalette };
}